import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import Segmented from "@/components/Segmented";
import Recolhivel from "@/components/Recolhivel";
import { gravarEscopo, comDe } from "@/lib/escritorioInfo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, History } from "lucide-react";

// Lote 164 — Controladoria: visão consolidada do escritório (prazos, audiências, vencidos sem baixa, revisões vencidas,
// itens sem responsável), por pessoa. Administradores sempre; outras pessoas só se um administrador indicar (na Equipe).
// Itens PARTICULARES nunca entram. Cada número é um atalho: abre a lista já filtrada pela pessoa.
const COLUNAS = [
  { k: "prazos", rot: "Prazos", to: "/audiencias?filter=prazos" },
  { k: "audiencias", rot: "Audiências", to: "/audiencias?filter=audiencias" },
  { k: "atrasados", rot: "Vencidos", to: "/audiencias?filter=prazos", forte: true },
  { k: "revisoes", rot: "Revisões", to: "/processos?filtro=prioritarios" },
  { k: "pendencias", rot: "Atas a conferir", to: "/revisao" },
];
const brDia = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ""); return m ? `${m[3]}/${m[2]}` : ""; };
const quando = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function Numero({ n, onClick, forte, testid, rotulo }) {
  if (!n) return <span className="text-stone-300" data-testid={testid}>0</span>;
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-label={rotulo}
      className={`min-w-[2.25rem] h-9 px-2 rounded-md font-semibold tabular-nums hover:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 ${forte ? "text-red-700" : "text-stone-900"}`}>{n}</button>
  );
}

export default function Controladoria() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [janela, setJanela] = useState("7");
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [aud, setAud] = useState(null);                 // auditoria (só administrador)
  const [audDias, setAudDias] = useState(30);
  const [audPessoa, setAudPessoa] = useState("");

  const load = useCallback(async () => {
    try { setD((await http.get("/escritorio/controladoria", { params: { janela } })).data); setErro(null); }
    catch (e) { setErro(e?.response?.status === 403 ? "A Controladoria é liberada pelo administrador do escritório." : "Não consegui carregar agora. Tente de novo em instantes."); }
  }, [janela]);
  useEffect(() => { load(); }, [load]);

  const carregarAud = useCallback(async () => {
    try { setAud((await http.get("/escritorio/auditoria", { params: { dias: audDias, ...(audPessoa ? { pessoa: audPessoa } : {}) } })).data); }
    catch { setAud({ erro: true, eventos: [], pessoas: [] }); }
  }, [audDias, audPessoa]);
  useEffect(() => { if (d?.pode_auditoria) carregarAud(); }, [d?.pode_auditoria, carregarAud]);

  const abrir = (to, pessoa) => { gravarEscopo({ escopo: "escritorio", responsavel: pessoa || "" }); navigate(to); };
  const mudarEmail = async (v) => {
    try { await http.put("/escritorio/controladoria/preferencias", { email: v }); setD((x) => ({ ...x, email_resumo: v })); toast.success(v ? "Resumo diário por e-mail ligado" : "Resumo diário por e-mail desligado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Não consegui salvar"); }
  };

  const cab = (
    <div className="mb-5">
      <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-gold-600 mb-1">Escritório</div>
      <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="controladoria-titulo">Controladoria</h1>
    </div>
  );
  if (erro) return <div className="max-w-4xl mx-auto p-4 md:p-8">{cab}<p className="text-sm text-stone-600" data-testid="controladoria-erro">{erro}</p></div>;
  if (!d) return <div className="max-w-4xl mx-auto p-4 md:p-8">{cab}<p className="text-sm text-stone-500">Carregando…</p></div>;

  const t = d.totais;
  const janelaRot = janela === "hoje" ? "hoje" : "nos próximos 7 dias";
  const tiles = [
    { k: "prazos", rot: "Prazos", n: t.prazos, sub: janela === "hoje" ? janelaRot : `${t.prazos_hoje} hoje`, to: "/audiencias?filter=prazos" },
    { k: "audiencias", rot: "Audiências", n: t.audiencias, sub: janelaRot, to: "/audiencias?filter=audiencias" },
    { k: "atrasados", rot: "Vencidos sem baixa", n: t.atrasados, sub: "últimos 15 dias úteis", to: "/audiencias?filter=prazos", forte: true },
    { k: "revisoes", rot: "Revisões vencidas", n: t.revisoes_vencidas, sub: d.escritorio.revisoes_vencidas ? `${d.escritorio.revisoes_vencidas} do escritório` : "prioritários", to: "/processos?filtro=prioritarios" },
    { k: "sem", rot: "Sem responsável", n: t.sem_responsavel, sub: "saiu do escritório ou é Apoio", to: null, forte: true },
  ];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8" data-testid="controladoria">
      {cab}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Segmented size="sm" fit testid="controladoria-janela" ariaLabel="Período" value={janela} onChange={setJanela}
          options={[{ key: "hoje", label: "Hoje", testid: "janela-hoje" }, { key: "7", label: "7 dias", testid: "janela-7" }]} />
        <p className="text-xs text-stone-500">Até {brDia(d.fim)} · não inclui itens marcados como particulares.</p>
      </div>

      {d.alertas.length > 0 && (
        <section aria-label="Alertas" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="controladoria-alertas">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-1"><AlertTriangle className="w-4 h-4" strokeWidth={2} /> Atenção</h2>
          <ul className="space-y-1">
            {d.alertas.map((a) => <li key={a.tipo} data-testid={`alerta-${a.tipo}`} className={`text-sm ${a.gravidade === "alta" ? "text-red-800 font-medium" : "text-amber-900"}`}>{a.texto}</li>)}
          </ul>
        </section>
      )}

      <section aria-label="Resumo" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="controladoria-tiles">
        {tiles.map((x) => {
          const corpo = (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">{x.rot}</div>
              <div className={`mt-1 font-display text-3xl font-bold tabular-nums ${x.forte && x.n ? "text-red-700" : "text-stone-900"}`} data-testid={`tile-${x.k}-n`}>{x.n}</div>
              <div className="text-[11px] text-stone-500 leading-tight">{x.sub}</div>
            </>
          );
          return x.to && x.n ? (
            <button key={x.k} type="button" data-testid={`tile-${x.k}`} onClick={() => abrir(x.to, "")} className="text-left rounded-lg border border-stone-200 bg-white p-3 hover:border-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">{corpo}</button>
          ) : <div key={x.k} data-testid={`tile-${x.k}`} className="rounded-lg border border-stone-200 bg-white p-3">{corpo}</div>;
        })}
      </section>

      <section aria-label="Por pessoa" className="rounded-xl border border-stone-200 bg-white overflow-x-auto" data-testid="controladoria-pessoas">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
              <th className="px-4 py-2 font-semibold">Pessoa</th>
              {COLUNAS.map((c) => <th key={c.k} className="px-2 py-2 font-semibold text-center">{c.rot}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {d.pessoas.map((p) => (
              <tr key={p.user_id} data-testid={`pessoa-${p.user_id}`}>
                <td className="px-4 py-2 font-semibold text-stone-900">{p.nome}{p.user_id === user?.user_id && <span className="ml-2 text-xs font-normal text-stone-500">(você)</span>}</td>
                {COLUNAS.map((c) => (
                  <td key={c.k} className="px-2 py-1 text-center">
                    <Numero n={p[c.k]} forte={c.forte} testid={`pessoa-${p.user_id}-${c.k}`} rotulo={`${c.rot} de ${p.nome}: abrir a lista`}
                      onClick={() => abrir(c.to, c.k === "pendencias" ? "" : p.user_id)} />
                  </td>
                ))}
              </tr>
            ))}
            {d.escritorio.revisoes_vencidas > 0 && (
              <tr data-testid="linha-escritorio" className="bg-stone-50">
                <td className="px-4 py-2 text-stone-700">Escritório <span className="block text-[11px] text-stone-500">quem revisar primeiro dá baixa</span></td>
                <td /><td /><td />
                <td className="px-2 py-1 text-center"><Numero n={d.escritorio.revisoes_vencidas} testid="escritorio-revisoes" rotulo="Revisões do escritório: abrir a lista" onClick={() => abrir("/processos?filtro=prioritarios", "")} /></td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <div className="mt-4">
        <Recolhivel titulo="Revisões vencidas" resumo={`${d.revisoes.length}`} icon={History} testid="controladoria-revisoes" preenchido={d.revisoes.length > 0 && d.revisoes.length <= 5}>
          {d.revisoes.length === 0 ? <p className="text-sm text-stone-500">Nenhuma revisão vencida.</p> : (
            <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200 bg-white">
              {d.revisoes.map((r) => (
                <li key={r.processo_id} data-testid={`rev-${r.processo_id}`} className="p-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-stone-900 break-words">{r.title}</div>
                    <div className="text-xs text-stone-500">{r.modo === "escritorio" ? "Revisão do escritório" : `Responsável: ${r.responsavel_nome || "—"}`} · {r.atraso <= 0 ? "vence hoje" : `venceu há ${r.atraso} ${r.atraso === 1 ? "dia" : "dias"}`}</div>
                  </div>
                  <Link to={comDe(`/processos/${r.processo_id}`, r.dono_id, user?.user_id)} className="text-sm text-stone-600 underline underline-offset-2 hover:text-stone-900" data-testid={`rev-abrir-${r.processo_id}`}>Abrir</Link>
                </li>
              ))}
            </ul>
          )}
        </Recolhivel>

        {d.pode_auditoria && (
          <Recolhivel titulo="Auditoria — quem fez o quê" resumo={aud && !aud.erro ? `${aud.eventos.length} nos últimos ${aud.dias} dias` : ""} testid="controladoria-auditoria">
            <div className="flex flex-wrap items-center gap-3 text-xs text-stone-500">
              <label>Pessoa
                <select data-testid="aud-pessoa" value={audPessoa} onChange={(e) => setAudPessoa(e.target.value)} className="ml-2 h-9 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-900">
                  <option value="">Todas</option>
                  {(aud?.pessoas || []).map((p) => <option key={p.user_id} value={p.user_id}>{p.nome}</option>)}
                </select>
              </label>
              <label>Período
                <select data-testid="aud-dias" value={audDias} onChange={(e) => setAudDias(Number(e.target.value))} className="ml-2 h-9 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-900">
                  <option value={7}>7 dias</option><option value={30}>30 dias</option><option value={90}>90 dias</option><option value={180}>180 dias</option>
                </select>
              </label>
            </div>
            {!aud ? <p className="text-sm text-stone-500">Carregando…</p> : aud.erro ? <p className="text-sm text-red-700">Não consegui carregar a auditoria.</p> : aud.eventos.length === 0 ? <p className="text-sm text-stone-500" data-testid="aud-vazia">Nada registrado neste período.</p> : (
              <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200 bg-white" data-testid="aud-lista">
                {aud.eventos.map((e, i) => (
                  <li key={`${e.quando}-${i}`} className="px-3 py-2 text-sm flex flex-wrap gap-x-3">
                    <span className="text-xs text-stone-500 tabular-nums w-28 shrink-0">{quando(e.quando)}</span>
                    <span className="min-w-0 flex-1 break-words"><b className="font-semibold text-stone-900">{e.autor}</b> — {e.texto}</span>
                  </li>
                ))}
              </ul>
            )}
            {aud?.truncado && <p className="text-[11px] text-stone-500">Mostrando os registros mais recentes. Escolha uma pessoa ou um período menor para ver o resto.</p>}
            <p className="text-[11px] text-stone-500">Só leitura. Mostra quem agiu e em qual conta, sem o conteúdo dos processos.</p>
          </Recolhivel>
        )}
      </div>

      <label className="mt-6 flex items-center gap-3 text-sm text-stone-700" data-testid="controladoria-email">
        <Switch checked={!!d.email_resumo} onCheckedChange={mudarEmail} aria-label="Receber o resumo diário por e-mail" data-testid="controladoria-email-switch" />
        <span>Receber um resumo por e-mail nos dias úteis, por volta das 7h10 (só quando houver algo a dizer; sem números de processo).</span>
      </label>
    </div>
  );
}
